import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faCode,
	faGears,
	faPhone,
	faToggleOff,
	faToggleOn,
} from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useState } from 'react';
import { Icon } from '../../components/base/Icon';
import { Button } from '../../components/forms/Button';
import type { SelectSearchGroup } from '../../components/forms/SelectSearch';
import { SelectSearch } from '../../components/forms/SelectSearch';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import type { AvailableDataSource } from './DynamicFieldValue';

interface PhoneNumberFieldProps {
	label: string;
	value: string;
	onChange: (value: string) => void;
	required?: boolean;
	availableData?: Array<AvailableDataSource>;
	description?: string;
	provisionedPhones: Array<{ phoneNumber: string }>;
	phonesLoading: boolean;
}

export function PhoneNumberField({
	label,
	value,
	onChange,
	required = false,
	availableData = [],
	description,
	provisionedPhones,
	phonesLoading,
}: PhoneNumberFieldProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// Determine if value is dynamic based on the {{ }} pattern
	const isDynamic =
		value != null ? value.startsWith('{{') && value.endsWith('}}') : false;

	// Local state for the dynamic mode toggle (controlled by the value pattern)
	const [isInDynamicMode, setIsInDynamicMode] = useState(isDynamic);

	// Sync mode state when value changes externally
	useEffect(() => {
		setIsInDynamicMode(isDynamic);
	}, [isDynamic]);

	const handleToggle = () => {
		setIsInDynamicMode(!isInDynamicMode);
		// Clear value when switching modes
		onChange('');
	};

	// Build data sources for dynamic mode
	const allDataSources: Array<AvailableDataSource> = [];

	if (availableData != null && availableData.length > 0) {
		allDataSources.push(...availableData);
	}

	// Add system variables if not already included
	const hasSystemVars = allDataSources.some((s) => {
		return (
			s.category === t('System Variables') ||
			s.category === t('Context Variables')
		);
	});

	if (!hasSystemVars) {
		allDataSources.push({
			category: t('System Variables'),
			icon: faGears,
			fields: [
				{
					key: 'system.currentDate',
					label: t('Current Date'),
					type: 'string',
					example: '2024-01-15',
				},
				{
					key: 'system.currentTime',
					label: t('Current Time'),
					type: 'string',
					example: '14:30:00',
				},
			],
		});
	}

	const renderStaticInput = () => {
		const phoneGroups: Array<SelectSearchGroup> = [
			{
				key: 'provisioned-phones',
				label: t('Provisioned Phone Numbers'),
				options: provisionedPhones.map((phone) => ({
					value: phone.phoneNumber,
					label: phone.phoneNumber,
				})),
			},
		];

		const hasPhones = provisionedPhones.length > 0;

		return (
			<div>
				<SelectSearch
					groups={phoneGroups}
					value={!isInDynamicMode ? value : ''}
					onChange={(newValue: string) => {
						onChange(newValue ?? '');
					}}
					placeholder={
						phonesLoading
							? t('Loading...')
							: hasPhones
								? t('Select a phone number')
								: t('No phone numbers available')
					}
					disabled={phonesLoading || !hasPhones}
					emptyMessage={t('No phone numbers found')}
					mode="static"
				/>
				{!hasPhones && !phonesLoading && (
					<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'No provisioned phone numbers available. Contact your administrator to provision phone numbers.',
						)}
					</div>
				)}
			</div>
		);
	};

	const renderDynamicInput = () => {
		// Transform data sources into groups for the SelectSearch
		const comboboxGroups: Array<SelectSearchGroup> = allDataSources
			.map((source) => ({
				key: source.category,
				label: source.category,
				// AvailableDataSource types icon as unknown; every producer supplies a
				// FontAwesome IconDefinition (same assertion DynamicFieldValue makes).
				icon: source.icon as IconDefinition | undefined,
				options: source.fields.map((field) => ({
					value: `{{${field.key}}}`,
					label: field.label,
					subtitle: field.displayKey ?? field.key,
					displayValue:
						field.displayKey != null
							? `{{${field.displayKey}}}`
							: `{{${field.key}}}`,
				})),
			}))
			.filter((group) => group.options.length > 0);

		const hasVariables =
			comboboxGroups.reduce((sum, group) => {
				return sum + group.options.length;
			}, 0) > 0;

		return (
			<div className="space-y-3">
				<div>
					<SelectSearch
						groups={comboboxGroups}
						value={value ?? ''}
						onChange={(newValue: string) => {
							onChange(newValue);
						}}
						placeholder={
							hasVariables
								? t('Search for a variable...')
								: t('No variables available')
						}
						disabled={!hasVariables}
						emptyMessage={t('No matching variables found')}
						mode="static"
					/>

					{!hasVariables && (
						<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{t(
								'No variables available from previous steps. Add steps to the flow to see available outputs.',
							)}
						</div>
					)}
				</div>
			</div>
		);
	};

	return (
		<div className="space-y-3">
			{/* Label and Toggle */}
			<div className="flex items-center justify-between">
				<div>
					<label
						className={`text-sm font-medium ${colors.classes.textPrimary}`}
					>
						{label}{' '}
						{required && <span className={colors.classes.statusError}>*</span>}
					</label>
					{description != null && (
						<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{description}
						</div>
					)}
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={handleToggle}
					className="flex items-center gap-2"
					title={
						isInDynamicMode
							? t('Switch to select phone number')
							: t('Switch to dynamic value')
					}
				>
					<Icon
						icon={isInDynamicMode ? faToggleOn : faToggleOff}
						className={
							isInDynamicMode
								? colors.classes.statusSuccess
								: colors.classes.textSecondary
						}
					/>
					<span className="text-xs">
						{isInDynamicMode ? t('Dynamic') : t('Static')}
					</span>
				</Button>
			</div>

			{/* Input Field */}
			<div>{isInDynamicMode ? renderDynamicInput() : renderStaticInput()}</div>

			{/* Mode Indicator */}
			<div
				className={`text-xs ${colors.classes.textSecondary} flex items-center gap-1`}
			>
				<Icon icon={isInDynamicMode ? faCode : faPhone} />
				<span>
					{isInDynamicMode
						? t('Value will be resolved from execution data')
						: t('Selected phone number will be used')}
				</span>
			</div>
		</div>
	);
}
